import { useEffect, useRef, useState } from "react";
import {
  getDocUrl,
  getPreviewProxyUrl,
  isHeicUrl,
} from "../../utils/docPreview";

function needsHeicHandling(url) {
  return isHeicUrl(url);
}

async function fetchProxyBlob(fileUrl) {
  const proxyUrl = getPreviewProxyUrl(fileUrl);
  if (!proxyUrl) throw new Error("No proxy URL");

  const token =
    (typeof window !== "undefined" &&
      window.localStorage.getItem("admin_token")) ||
    "";

  const res = await fetch(proxyUrl, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    cache: "no-store",
  });

  if (!res.ok) throw new Error(`Proxy ${res.status}`);
  const blob = await res.blob();
  if (!blob || blob.size === 0) throw new Error("Empty proxy response");
  return blob;
}

async function convertHeicBlobClient(blob) {
  const heic2any = (await import("heic2any")).default;
  const converted = await heic2any({
    blob,
    toType: "image/jpeg",
    quality: 0.85,
  });
  return Array.isArray(converted) ? converted[0] : converted;
}

export default function SmartImage({
  src,
  alt = "",
  className = "",
  style,
  referrerPolicy = "no-referrer",
  loading,
  fallback = null,
  onClick,
  ...props
}) {
  const rawSrc = getDocUrl(src) || (fallback ? String(fallback) : "");
  const [displaySrc, setDisplaySrc] = useState(() =>
    needsHeicHandling(rawSrc) ? "" : rawSrc || ""
  );
  const [failed, setFailed] = useState(false);
  const objectUrlRef = useRef(null);
  const genRef = useRef(0);

  useEffect(() => {
    const gen = ++genRef.current;
    let cancelled = false;

    const revoke = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };

    const run = async () => {
      revoke();
      setFailed(false);

      const url = String(rawSrc || "").trim();
      if (!url) {
        if (fallback) {
          setDisplaySrc(String(fallback));
        } else {
          setDisplaySrc("");
        }
        return;
      }

      if (!needsHeicHandling(url)) {
        setDisplaySrc(url);
        return;
      }

      setDisplaySrc("");

      try {
        const blob = await fetchProxyBlob(url);
        if (cancelled || gen !== genRef.current) return;
        revoke();
        const obj = URL.createObjectURL(blob);
        objectUrlRef.current = obj;
        setDisplaySrc(obj);
        return;
      } catch {
        /* client convert fallback */
      }

      try {
        const upstream = await fetch(url);
        const blob = await upstream.blob();
        const jpeg = await convertHeicBlobClient(blob);
        if (cancelled || gen !== genRef.current) return;
        revoke();
        const obj = URL.createObjectURL(jpeg);
        objectUrlRef.current = obj;
        setDisplaySrc(obj);
      } catch {
        if (!cancelled && gen === genRef.current) {
          if (fallback) {
            setDisplaySrc(String(fallback));
          } else {
            setFailed(true);
          }
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      revoke();
    };
  }, [rawSrc, fallback]);

  const handleError = async () => {
    if (fallback && displaySrc !== fallback) {
      setDisplaySrc(String(fallback));
      return;
    }
    if (!rawSrc || objectUrlRef.current) {
      if (fallback) {
        setDisplaySrc(String(fallback));
      } else {
        setFailed(true);
      }
      return;
    }
    if (!rawSrc.startsWith("http://") && !rawSrc.startsWith("https://")) {
      if (fallback) {
        setDisplaySrc(String(fallback));
      } else {
        setFailed(true);
      }
      return;
    }
    try {
      const blob = await fetchProxyBlob(rawSrc);
      const obj = URL.createObjectURL(blob);
      objectUrlRef.current = obj;
      setDisplaySrc(obj);
    } catch {
      if (fallback) {
        setDisplaySrc(String(fallback));
      } else {
        setFailed(true);
      }
    }
  };

  if (failed && !fallback) {
    return (
      <div
        className={`flex items-center justify-center bg-slate-100 text-[10px] font-medium text-slate-400 ${className || ""}`}
        style={style}
        role="img"
        aria-label={alt || "Image unavailable"}
        onClick={onClick}
      >
        —
      </div>
    );
  }

  if (!displaySrc) {
    if (fallback) {
      return (
        <img
          src={fallback}
          alt={alt}
          className={className}
          style={style}
          onClick={onClick}
          {...props}
        />
      );
    }
    return (
      <div
        className={`animate-pulse bg-slate-100 ${className || ""}`}
        style={style}
        aria-hidden
        onClick={onClick}
      />
    );
  }

  return (
    <img
      src={displaySrc}
      alt={alt}
      className={className}
      style={style}
      referrerPolicy={referrerPolicy}
      loading={loading}
      onError={handleError}
      onClick={onClick}
      {...props}
    />
  );
}
